/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, of } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';
import Highcharts from 'highcharts/highmaps';
import AccessibilityModule from 'highcharts/modules/accessibility';
import { EntityInfoService } from 'src/app/shared/services/metadata-api/entity-info.service';
import { SentimentService } from 'src/app/shared/services/data-api/sentiment.service';
import { Category } from '../market-board.types';
import { pickDelta, pickScore, pickVolume } from '../market-board.utils';
import { COUNTRY_NAME_TO_HC_KEY } from './country-iso-map';
import { CountryBoardDataService } from './country-board-data.service';

// Register the accessibility module on this component's Highcharts (highmaps)
// instance so the sentiment map is keyboard- and screen-reader-navigable.
AccessibilityModule(Highcharts);

interface CountrySentiment {
  value: number;
  delta: number;
  count: number;
}

interface TopEventVM {
  name: string;
  bucket: 'neg' | 'neu' | 'pos';
  mentions: number;
}

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-news-market-countries',
  templateUrl: './news-market-countries.component.html',
  styleUrls: ['./news-market-countries.component.scss'],
})
export class NewsMarketCountriesComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() categories: Category[] = [];
  @Input() searchTerm = '';
  @Input() sentimentParams: Record<string, any> = {};

  @Output() panelChanged = new EventEmitter<Category | null>();

  @ViewChild('mapContainer') mapContainer?: ElementRef<HTMLDivElement>;

  selectedCategory: Category | null = null;

  // Sentiment range filter (0–100%): countries whose average sentiment falls
  // outside [sentimentMin, sentimentMax] are dimmed on the map.
  sentimentMin = 0;
  sentimentMax = 100;

  // Map skeleton: shown until the map first paints with data, and while an
  // industry overlay is being fetched. Kept up for at least minSkeletonMs so a
  // fast/cached load still shows the loading state instead of flashing.
  mapLoading = true;
  private mapLoadStart = Date.now();
  private readonly minSkeletonMs = 600;

  private mapChart?: Highcharts.MapChart;
  private worldTopology: any = null;
  private allCountries: {
    alpha_id: string;
    compound_key: string;
    name: string;
  }[] = [];
  private countrySentiment: Record<string, CountrySentiment> = {};
  private countrySentimentSig = '';
  // Last sentimentParams signature acted on. The parent passes sentimentParams
  // as a fresh object every change-detection cycle, so ngOnChanges fires
  // constantly; gating on the value signature stops a setData/refetch storm.
  private lastParamsSig = '';

  // hc-key of the search match (others are dimmed); null when no active search.
  private searchMatchKey: string | null = null;

  private prefetchTimer?: ReturnType<typeof setTimeout>;
  // Top key event per hovered hc-key for the tooltip (null = no events; absent = not fetched).
  private topEventByHcKey = new Map<string, TopEventVM | null>();

  private destroy$ = new Subject<void>();

  constructor(
    private entityInfo: EntityInfoService,
    private sentimentService: SentimentService,
    private data: CountryBoardDataService,
    private http: HttpClient,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    // Disable the map series' default "raise hovered country to top" behaviour.
    // It re-orders the country's SVG node on every hover, which (a) makes the
    // browser drop the click — mousedown and mouseup land on a moved node, so no
    // `click` fires — and (b) floods Sentry Session Replay with DOM mutations,
    // janking the page. It's read from the point-class prototype, so per-point
    // options can't override it; patch the prototype once.
    const mapPointProto = (Highcharts as any).seriesTypes?.map?.prototype
      ?.pointClass?.prototype;
    if (mapPointProto && 'moveToTopOnHover' in mapPointProto) {
      mapPointProto.moveToTopOnHover = false;
    }

    this.loadAllCountries();
    setTimeout(() => this.renderMap(), 0);

    // Open the country panel ourselves on pointerup rather than via a `click`
    // event: the map never fires a real `click` (its hovered node re-orders
    // between press and release), and other layers — the accessibility proxies,
    // Highcharts' own handlers — can intercept the target. So we listen on the
    // document in capture, and resolve the country from whatever SVG path sits
    // under the pointer, ignoring the event target entirely.
    document.addEventListener('pointerdown', this.onMapPointerDown, true);
    document.addEventListener('pointerup', this.onMapPointerUp, true);
  }

  private mapPointerDownX = 0;
  private mapPointerDownY = 0;

  private onMapPointerDown = (event: PointerEvent): void => {
    this.mapPointerDownX = event.clientX;
    this.mapPointerDownY = event.clientY;
  };

  private onMapPointerUp = (event: PointerEvent): void => {
    // Ignore drags (map pan) — only a near-stationary press counts as a click.
    if (
      Math.abs(event.clientX - this.mapPointerDownX) > 5 ||
      Math.abs(event.clientY - this.mapPointerDownY) > 5
    ) {
      return;
    }
    const root = this.mapContainer?.nativeElement;
    if (!root) return;
    const key = this.countryKeyAt(event.clientX, event.clientY, root);
    if (!key) return;
    // Board countries carry rich data; any other clicked country gets a
    // category built on the fly so its panel still opens.
    const cat =
      this.categories.find((c) => this.resolveHcKey(c) === key) ??
      this.buildCountryCategory(key);
    if (cat) this.zone.run(() => this.openPanel(cat));
  };

  // hc-key of the country whose SVG path sits under the given screen point.
  // Scans the whole element stack (so accessibility proxies / overlays over the
  // map don't hide the path) and stays within this component's map.
  private countryKeyAt(x: number, y: number, root: HTMLElement): string | null {
    for (const el of document.elementsFromPoint(x, y)) {
      if (!root.contains(el)) continue;
      const match = /highcharts-key-([^\s]+)/.exec(
        el.getAttribute?.('class') ?? '',
      );
      if (match) return match[1];
    }
    return null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sentimentParams'] && !changes['sentimentParams'].firstChange) {
      // The parent hands us a new sentimentParams object every change-detection
      // cycle; only react when the actual values change, or we'd re-fetch and
      // re-`setData` the whole map on every cycle (touch-listener storm + jank).
      const sig = this.sentimentSignature();
      if (sig !== this.lastParamsSig) {
        this.lastParamsSig = sig;
        this.loadAllCountrySentiment();
      }
    }
    if (changes['categories'] && !changes['categories'].firstChange) {
      this.updateMapData();
    }
    if (changes['searchTerm'] && !changes['searchTerm'].firstChange) {
      this.highlightSearch();
    }
  }

  // True when the sentiment range is narrower than the full 0–100% span.
  get sentimentFilterActive(): boolean {
    return this.sentimentMin > 0 || this.sentimentMax < 100;
  }

  onSentimentMinChange(value: string | number): void {
    // Clamp so the low thumb can't cross above the high thumb.
    this.sentimentMin = Math.min(Number(value), this.sentimentMax);
    this.updateMapData();
    this.cdr.markForCheck();
  }

  onSentimentMaxChange(value: string | number): void {
    this.sentimentMax = Math.max(Number(value), this.sentimentMin);
    this.updateMapData();
    this.cdr.markForCheck();
  }

  resetSentimentRange(): void {
    this.sentimentMin = 0;
    this.sentimentMax = 100;
    this.updateMapData();
    this.cdr.markForCheck();
  }

  private highlightSearch(): void {
    const series: any = this.mapChart?.series?.[0];
    if (!series) return;

    const q = this.searchTerm.trim().toLowerCase();
    const points: any[] = series.points ?? [];
    const match = q
      ? (points.find((p) => (p.name ?? '').toLowerCase().startsWith(q)) ??
        points.find((p) => (p.name ?? '').toLowerCase().includes(q)))
      : null;

    this.searchMatchKey = match
      ? (match['hc-key'] ?? match.properties?.['hc-key'] ?? null)
      : null;
    this.updateMapData();
  }

  ngOnDestroy(): void {
    clearTimeout(this.prefetchTimer);
    document.removeEventListener('pointerdown', this.onMapPointerDown, true);
    document.removeEventListener('pointerup', this.onMapPointerUp, true);
    // Destroy the chart so a re-mount doesn't leak an orphan Highcharts instance.
    this.mapChart?.destroy();
    this.mapChart = undefined;
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Selection just sets the bound entity; the shared detail panel loads its own
  // constituents + key events (over the default 7-day window) when it changes.
  openPanel(cat: Category): void {
    cat.loading = false;
    this.selectedCategory = cat;
    this.panelChanged.emit(cat);
  }

  closePanel(): void {
    this.selectedCategory = null;
    this.panelChanged.emit(null);
  }

  /** Debounced hover-prefetch so a quick mouse sweep doesn't fire many fetches. */
  private schedulePrefetch(hcKey: string): void {
    clearTimeout(this.prefetchTimer);
    this.prefetchTimer = setTimeout(() => this.prefetch(hcKey), 200);
  }

  /** Warm the caches for a hovered country so its panel opens instantly. */
  private prefetch(hcKey: string): void {
    const cat =
      this.categories.find((c) => this.resolveHcKey(c) === hcKey) ??
      this.buildCountryCategory(hcKey);
    if (!cat) return;
    const id = this.requestId(cat).toLowerCase();
    // Subscribe so the shareReplay observables run and retain their result.
    this.data.getChildren$(id).pipe(takeUntil(this.destroy$)).subscribe();
    this.data
      .getKeyEvents$(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((events) => {
        const top = events[0];
        this.topEventByHcKey.set(
          hcKey,
          top
            ? {
                name: top.name,
                bucket:
                  top.contribution < 0
                    ? 'neg'
                    : top.contribution > 0
                      ? 'pos'
                      : 'neu',
                mentions: top.mentions,
              }
            : null,
        );
        // If still hovering this country, refresh the tooltip to show it.
        const hp: any = this.mapChart?.hoverPoint;
        if (hp && (hp['hc-key'] ?? hp.properties?.['hc-key']) === hcKey) {
          (this.mapChart as any)?.tooltip?.refresh(hp);
        }
      });
  }

  private loadAllCountries(): void {
    if (this.allCountries.length > 0) return;
    this.entityInfo
      .getCountries()
      .pipe(
        catchError((err) => {
          console.error('[market-board] getCountries failed', err);
          return of({ entities: [] } as any);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((res: any) => {
        const list = (res?.entities ?? []) as any[];
        this.allCountries = list
          .filter((e) => e?.alpha_id)
          .map((e) => ({
            alpha_id: String(e.alpha_id).toLowerCase(),
            compound_key: e.compound_key,
            name: e.name ?? e.compound_key ?? e.alpha_id,
          }));
        this.loadAllCountrySentiment();
        this.updateMapData();
      });
  }

  private loadAllCountrySentiment(): void {
    const keys = this.allCountries
      .map((c) => c.compound_key)
      .filter((k): k is string => !!k);
    if (keys.length === 0) return;

    const sig = this.sentimentSignature();
    if (sig === this.countrySentimentSig) return;
    this.countrySentimentSig = sig;

    this.sentimentService
      .getSentimentItemized(keys, this.sentimentParams)
      .pipe(
        catchError((err) => {
          console.error(
            '[market-board] country sentiment_itemized failed',
            err,
          );
          return of({} as Record<string, any>);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((sMap: Record<string, any>) => {
        if (!sMap || Object.keys(sMap).length === 0) return;
        const next: Record<string, CountrySentiment> = {};
        Object.entries(sMap).forEach(([key, s]) => {
          const score = pickScore(s);
          if (!Number.isFinite(score)) return;
          const delta = pickDelta(s);
          next[key.toLowerCase()] = {
            value: Math.round(score * 100),
            delta: Number.isFinite(delta) ? Math.round(delta * 100) : 0,
            count: pickVolume(s),
          };
        });
        this.countrySentiment = { ...this.countrySentiment, ...next };
        this.updateMapData();
      });
  }

  private sentimentSignature(): string {
    const p = this.sentimentParams ?? {};
    return JSON.stringify([
      p['period'],
      p['time'],
      p['lang'],
      p['feed'],
      p['categories'],
      p['continents'],
      p['countries'],
      p['factuality'],
      p['temporality'],
      p['ranks'],
      p['panels'],
      p['from'],
      p['to'],
      p['date_from'],
      p['date_to'],
    ]);
  }

  private requestId(cat: Category): string {
    const ck = cat.compoundKey;
    if (ck && ck.includes(':')) return ck.split(':').slice(1).join(':');
    return cat.id;
  }

  private renderMap(): void {
    if (!this.mapContainer?.nativeElement) return;

    // Captured for Highcharts point callbacks that need `this` = the point.
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const component = this;

    const draw = (topo: any) => {
      if (!this.mapContainer?.nativeElement) return;
      // Never leave a previous chart on the same container: a second instance
      // stacks an orphan SVG over the map that swallows every real click while
      // the live handler (on the newest chart) never sees them.
      this.mapChart?.destroy();
      const data = this.buildMapData();
      if (data.some((d) => d.value != null)) {
        this.finishMapLoading();
      }
      this.mapChart = Highcharts.mapChart(this.mapContainer.nativeElement, {
        chart: {
          map: topo as any,
          backgroundColor: 'transparent',
          spacing: [8, 8, 8, 8],
        },
        title: { text: undefined },
        credits: { enabled: false },
        accessibility: {
          enabled: true,
          description:
            'Choropleth world map. Each country is shaded by the average ' +
            'news sentiment of its constituents, from negative (red) to ' +
            'positive (green). Selecting a country opens its detail panel.',
          keyboardNavigation: { enabled: true },
          point: {
            valueDescriptionFormat:
              '{point.name}: sentiment {point.value} percent.',
          },
        },
        mapNavigation: {
          enabled: true,
          buttonOptions: { verticalAlign: 'bottom' },
        },
        colorAxis: {
          min: 25,
          max: 75,
          stops: [
            [0, '#ea6b6e'],
            [0.5, '#e8a942'],
            [1, '#2ecc9a'],
          ],
          labels: {
            style: {
              color: 'rgba(229, 231, 234, 0.55)',
              fontSize: '10px',
            },
            format: '{value}%',
          },
        },
        legend: { enabled: false },
        tooltip: {
          useHTML: true,
          backgroundColor: '#363636',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 8,
          padding: 10,
          style: { color: '#e5e7ea' },
          headerFormat: '',
          pointFormatter: function (this: any): string {
            const name =
              this.name || this.properties?.name || this.options?.name || '';
            const title =
              '<div style="font-weight:700;font-size:13px;color:#fff;">' +
              name +
              '</div>';
            if (this.value === null || this.value === undefined) {
              return (
                '<div style="min-width:170px;">' +
                title +
                '<div style="font-size:11px;color:rgba(229,231,234,0.55);' +
                'margin-top:4px;">No coverage</div></div>'
              );
            }
            const key = this['hc-key'] ?? this.properties?.['hc-key'];
            const loaded = component.topEventByHcKey.has(key);
            const top = component.topEventByHcKey.get(key);
            const colors = { neg: '#ea6b6e', neu: '#e8a942', pos: '#2ecc9a' };

            let eventHtml: string;
            if (!loaded) {
              eventHtml =
                '<span style="font-size:11px;opacity:0.6;">loading…</span>';
            } else if (!top) {
              eventHtml =
                '<span style="font-size:11px;opacity:0.6;">no key events</span>';
            } else {
              eventHtml =
                '<span style="display:inline-block;background:' +
                colors[top.bucket as 'neg' | 'neu' | 'pos'] +
                ';color:#1a1a1a;font-size:11px;font-weight:600;' +
                'padding:2px 8px;border-radius:10px;">' +
                top.name +
                '</span>' +
                '<span style="margin-left:7px;font-size:11px;' +
                'color:rgba(229,231,234,0.6);">' +
                top.mentions.toLocaleString() +
                ' mentions</span>';
            }
            return (
              '<div style="min-width:170px;">' +
              title +
              '<div style="font-size:12px;margin:5px 0 8px;">Sentiment ' +
              '<b>' +
              this.value +
              '%</b> <span style="color:rgba(229,231,234,0.6);">(' +
              this.deltaLabel +
              ')</span></div>' +
              '<div style="display:flex;align-items:center;flex-wrap:wrap;">' +
              eventHtml +
              '</div></div>'
            );
          },
        },
        series: [
          {
            type: 'map',
            name: 'Sentiment',
            data,
            joinBy: ['hc-key', 'hc-key'],
            nullColor: '#4a4e57',
            borderColor: '#5a5f68',
            borderWidth: 0.5,
            cursor: 'pointer',
            allowPointSelect: true,
            nullInteraction: true,
            states: {
              hover: { borderColor: '#e5e7ea', borderWidth: 1 },
              select: { color: null, borderColor: '#ffffff', borderWidth: 2.5 },
            },

            // Country selection is handled by our own document pointerup
            // listener (see onMapPointerUp) — the map series never fires a real
            // `click` because it re-orders the hovered path between press and
            // release.
            point: {
              events: {
                // Hover → warm the caches so the panel opens instantly on click.
                mouseOver: function (this: any): void {
                  const key = this['hc-key'] ?? this.properties?.['hc-key'];
                  if (key) component.schedulePrefetch(key);
                },
              },
            },
          } as any,
        ],
      });
    };

    if (this.worldTopology) {
      draw(this.worldTopology);
      return;
    }

    this.http
      .get('https://code.highcharts.com/mapdata/custom/world.topo.json')
      .pipe(
        // ng-http-caching can emit the cached value AND the network value;
        // take(1) so the map is drawn exactly once (a second draw = orphan chart).
        take(1),
        takeUntil(this.destroy$),
        catchError(() => of(null)),
      )
      .subscribe((topo) => {
        if (!topo) {
          console.error('[market-board] failed to load world topology');
          this.mapLoading = false;
          this.cdr.markForCheck();
          return;
        }
        this.worldTopology = topo;
        draw(topo);
      });
  }

  private updateMapData(): void {
    if (!this.mapChart || !this.mapChart.series?.[0]) return;
    const data = this.buildMapData();
    // Map series always carries one point per topology area; setData joins the
    // new values in place (updatePoints=true) which preserves the series click.
    (this.mapChart.series[0] as any).setData(data, true, false, true);
    // First paint with real values → drop the skeleton.
    if (data.some((d) => d.value != null)) {
      this.finishMapLoading();
    }
  }

  // Hide the loading skeleton, but keep it visible for at least minSkeletonMs
  // from load start so a fast/cached load doesn't flash the loading state.
  private finishMapLoading(): void {
    if (!this.mapLoading) return;
    const remaining = this.minSkeletonMs - (Date.now() - this.mapLoadStart);
    if (remaining <= 0) {
      this.mapLoading = false;
      this.cdr.markForCheck();
    } else {
      setTimeout(() => {
        this.mapLoading = false;
        this.cdr.markForCheck();
      }, remaining);
    }
  }

  private countryHcKey(c: { alpha_id: string; name: string }): string | null {
    const alpha = (c.alpha_id || '').toLowerCase();
    if (COUNTRY_NAME_TO_HC_KEY[alpha]) return COUNTRY_NAME_TO_HC_KEY[alpha];
    if (alpha.length === 2) return alpha;
    const name = (c.name || '').toLowerCase();
    if (COUNTRY_NAME_TO_HC_KEY[name]) return COUNTRY_NAME_TO_HC_KEY[name];
    return null;
  }

  private buildCountryCategory(hcKey: string): Category | null {
    const key = hcKey.toLowerCase();
    const country = this.allCountries.find((c) => this.countryHcKey(c) === key);
    if (!country) return null;
    const s = this.countrySentiment[country.compound_key?.toLowerCase() ?? ''];
    return {
      id: country.alpha_id,
      type: 'country',
      compoundKey: country.compound_key,
      name: country.name,
      hcKey: key,
      constituentCount: 0,
      neg: 0,
      neu: 0,
      pos: 0,
      spread: 0,
      average: s?.value ?? 0,
      delta: s?.delta ?? 0,
      movers: { up: 0, down: 0 },
      topEvent: undefined,
      constituents: [],
      children: [],
      expanded: false,
      loading: false,
    };
  }

  private buildMapData(): any[] {
    const bySentimentKey = new Map<string, Category>();
    this.categories.forEach((cat) => {
      if (cat.compoundKey) {
        bySentimentKey.set(cat.compoundKey.toLowerCase(), cat);
      }
      if (cat.id) {
        bySentimentKey.set(String(cat.id).toLowerCase(), cat);
      }
    });

    const seen = new Set<string>();
    const out: any[] = [];

    const push = (
      hcKey: string,
      name: string,
      cat: Category | null,
      sentiment?: CountrySentiment,
    ) => {
      if (seen.has(hcKey)) return;
      seen.add(hcKey);
      const value = cat ? cat.average : (sentiment?.value ?? null);
      // Dim a country when it doesn't match the active search, or when a
      // sentiment range filter is active and its value falls outside the range
      // (countries with no coverage have no value and are filtered out too).
      const outOfRange =
        this.sentimentFilterActive &&
        (value == null ||
          value < this.sentimentMin ||
          value > this.sentimentMax);
      const dimmed =
        (this.searchMatchKey && hcKey !== this.searchMatchKey) || outOfRange;
      const dimColor = dimmed ? 'rgba(74, 78, 87, 0.35)' : undefined;
      if (cat) {
        out.push({
          'hc-key': hcKey,
          name,
          value: cat.average,
          color: dimColor,
          constituents: cat.constituentCount,
          delta: cat.delta,
          deltaLabel: (cat.delta > 0 ? '+' : '') + cat.delta + '%',
          topEvent: cat.topEvent?.label ?? '—',
        });
      } else if (sentiment) {
        out.push({
          'hc-key': hcKey,
          name,
          value: sentiment.value,
          color: dimColor,
          constituents: sentiment.count,
          delta: sentiment.delta,
          deltaLabel: (sentiment.delta > 0 ? '+' : '') + sentiment.delta + '%',
          topEvent: '—',
        });
      } else {
        out.push({
          'hc-key': hcKey,
          name,
          value: null,
          color: dimColor,
          constituents: 0,
          deltaLabel: '—',
          topEvent: 'No coverage',
        });
      }
    };

    this.allCountries.forEach((c) => {
      const hc = this.countryHcKey(c);
      if (!hc) return;
      const compoundKey = c.compound_key?.toLowerCase() ?? '';
      const cat =
        bySentimentKey.get(compoundKey) ?? bySentimentKey.get(hc) ?? null;
      const sentiment = cat ? undefined : this.countrySentiment[compoundKey];
      push(hc, c.name, cat, sentiment);
    });

    this.categories.forEach((cat) => {
      const key = this.resolveHcKey(cat);
      if (!key) return;
      push(key, cat.name, cat);
    });

    return out;
  }

  private resolveHcKey(cat: Category): string | null {
    if (cat.hcKey) return cat.hcKey.toLowerCase();
    const raw = (cat.id || cat.name || '').toLowerCase();
    if (COUNTRY_NAME_TO_HC_KEY[raw]) return COUNTRY_NAME_TO_HC_KEY[raw];
    if (raw.length === 2) return raw;
    const nameKey = (cat.name || '').toLowerCase();
    if (COUNTRY_NAME_TO_HC_KEY[nameKey]) return COUNTRY_NAME_TO_HC_KEY[nameKey];
    return null;
  }
}
